<template>
  <app-page>
    <template v-slot:custom-content>
      <dashboard-terms-modal />
      <terms-amendment-modal
        v-if="shouldAcceptDbTermsOfBusinessAmendment"
        :termsAmendment="DB_TERMS_OF_BUSINESS_AMENDMENT"
      />
      <terms-amendment-modal
        v-if="shouldAcceptSalaryServiceAddendumDb"
        :termsAmendment="DB_SALARY_SERVICE_ADDENDUM"
        :canDismiss="false"
      />
      <terms-amendment-modal
        v-if="shouldAcceptMhrTermsOfBusinessAmendment"
        :termsAmendment="MHR_TERMS_OF_BUSINESS_AMENDMENT"
      />
      <terms-amendment-modal
        v-if="shouldAcceptHealthcareTermsOfBusinessAmendment"
        :termsAmendment="HEALTHCARE_TERMS_OF_BUSINESS_AMENDMENT"
      />
      <terms-amendment-modal
        v-if="shouldAcceptLoopAgreement"
        :termsAmendment="LOOP_AGREEMENT"
        acceptText="Yes, I consent"
        dismissText="Dismiss"
      />
      <terms-amendment-modal
        v-if="shouldAcceptW2UpgradeNotification"
        :termsAmendment="W2_UPGRADE_NOTIFICATION"
        acceptText="I understand"
        dismissText="Dismiss"
      />
      <terms-amendment-modal v-if="shouldAcceptMyviewPayNowAmendment" :termsAmendment="MYVIEW_PAYNOW_AMENDMENT" />
      <b-container v-if="isEnableSmeLaunchV2">
        <SmeCard>
          <b-row>
            <b-col>
              <b-card-text>
                <h1 class="h3">Welcome to Stream! 🚀</h1>
                <p>
                  Congratulations! You're one step closer to giving your employees access to fair financial services.
                </p>
                <p>
                  We've sent an email to <a href="">{{ State?.state?.user?.email }}</a> with instructions on how to get
                  started.
                </p>
              </b-card-text>
            </b-col>
          </b-row>
        </SmeCard>
      </b-container>
      <b-container class="pb-4" v-else>
        <sme-tour v-if="isTourApplicable" :tour="dashboardTour" class="mt-n4 mb-3" id="dashboard-tour" auto-start />

        <div class="scroll-pills" :class="{ 'scroll-pills--stuck': isStuck }" ref="pillsRef">
          <button
            v-for="pill in visiblePills"
            :key="pill.id"
            class="scroll-pill"
            :class="{ 'scroll-pill--active': activeSection === pill.id }"
            @click="scrollToSection(pill.id)"
          >
            {{ pill.label }}
          </button>
        </div>

        <dashboard-header-stats class="mb-2" />
        <b-row>
          <b-col lg="6" class="mb-4 mb-lg-0">
            <div id="section-tasks" class="mb-4">
              <dashboard-todos v-if="showDashboardTodos" data-id="dashboard.todos" />
            </div>
            <div id="section-pay">
              <dashboard-payschedules v-if="State.state.claims.a" data-id="dashboard.payschedules" />
            </div>
          </b-col>
          <b-col lg="6">
            <div id="section-shifts" class="mb-4">
              <dashboard-shifts data-id="dashboard.shifts" />
            </div>
            <div id="section-employees">
              <dashboard-employees data-id="dashboard.employees" />
            </div>
          </b-col>
        </b-row>
        <div v-if="portalUpdatesJan2026Enabled" id="section-impact" class="mt-4">
          <dashboard-impact class="mb-4" />
        </div>
        <dashboard-adoption
          v-if="isActivating && !isSelfFunded && isEnableAdoptionGamification"
          id="section-adoption"
          class="mt-4 p-0"
        />
      </b-container>
    </template>
  </app-page>
</template>

<script setup>
import { computed, ref, onMounted, onBeforeUnmount } from 'vue';
import AppPage from '@/components/AppPage.vue';
import SmeTour from '@/components/Tour.vue';
import useFeatureFlages from '@/composables/useFeatureFlags';
import DashboardAdoption from '@/pages/dashboard/components/DashboardAdoption.vue';
import DashboardHeaderStats from '@/pages/dashboard/components/DashboardHeaderStats.vue';
import DashboardImpact from '@/pages/dashboard/components/DashboardImpact.vue';
import TermsAmendmentModal from '@/pages/dashboard/components/TermsAmendmentModal.vue';
import DashboardTermsModal from '@/pages/dashboard/components/DashboardTermsModal.vue';
import DashboardEmployees from '@/pages/dashboard/components/employees/DashboardEmployees.vue';
import DashboardPayschedules from '@/pages/dashboard/components/payschedules/DashboardPayschedules.vue';
import DashboardShifts from '@/pages/dashboard/components/shifts/DashboardShifts.vue';
import DashboardTodos from '@/pages/dashboard/components/todos/DashboardTodos.vue';
import dashboardTour from '@/pages/dashboard/dashboardTour';
import State from '@/state/State';
import useEmployees from '@/state/composables/useEmployees';
import useLaunchState from '@/state/composables/useLaunchState';
import { TODO_ACTIONS } from '@/utils/Todo';
import {
  DB_SALARY_SERVICE_ADDENDUM,
  DB_TERMS_OF_BUSINESS_AMENDMENT,
  MHR_TERMS_OF_BUSINESS_AMENDMENT,
  HEALTHCARE_TERMS_OF_BUSINESS_AMENDMENT,
  LOOP_AGREEMENT,
  W2_UPGRADE_NOTIFICATION,
  MYVIEW_PAYNOW_AMENDMENT,
} from '@/utils/agreements';
import SmeCard from '@/components/atoms/SmeCard.vue';

const {
  isSelfFunded,
  isEnableAdoptionGamification,
  isEnableSmeLaunchV2,
  portalUpdatesJan2026Enabled,
  shouldAcceptDbTermsOfBusinessAmendment,
  shouldAcceptSalaryServiceAddendumDb,
  shouldAcceptMhrTermsOfBusinessAmendment,
  shouldAcceptHealthcareTermsOfBusinessAmendment,
  shouldAcceptLoopAgreement,
  shouldAcceptW2UpgradeNotification,
  shouldAcceptMyviewPayNowAmendment,
} = useFeatureFlages();

const TOUR_TODO_ACTIONS = [
  TODO_ACTIONS.CONFIGURE_PAYSCHEDULE,
  TODO_ACTIONS.ADD_EMPLOYEES,
  TODO_ACTIONS.KYC_PENDING,
  TODO_ACTIONS.FUND_PENDING,
];

const { getEnrolmentSnapshot } = useEmployees();
const { getLaunchState, hasTodoAction, isActivating, outstandingTodos } = useLaunchState();

const isTourApplicable = computed(
  () =>
    TOUR_TODO_ACTIONS.some(hasTodoAction) &&
    // ensure that tour does not display over terms modal
    !(outstandingTodos.value ? outstandingTodos?.value.find(t => t.action === 'accept.terms') !== undefined : true),
);

Promise.all([getEnrolmentSnapshot(), getLaunchState()]);

const showDashboardTodos =
  State.state.claims.a ||
  (State.state.claims.m && State.state.company?.properties?.portal?.enable_portal_tasks_for_managers);

// Scroll pills
const pillsRef = ref(null);
const isStuck = ref(false);
const activeSection = ref(null);

const allPills = [
  { id: 'section-tasks', label: 'Actions', condition: () => showDashboardTodos },
  { id: 'section-shifts', label: 'Shifts', condition: () => true },
  { id: 'section-pay', label: 'Pay', condition: () => State.state.claims.a },
  { id: 'section-employees', label: 'Enrolment', condition: () => true },
  { id: 'section-impact', label: 'Impact', condition: () => portalUpdatesJan2026Enabled.value },
];

const visiblePills = computed(() => allPills.filter(p => p.condition()));

function scrollToSection(id) {
  const el = document.getElementById(id);
  if (!el) {
    return;
  }
  const offset = 60;
  const top = el.getBoundingClientRect().top + window.scrollY - offset;
  window.scrollTo({ top, behavior: 'smooth' });
}

let observer;
let stuckObserver;

onMounted(() => {
  // Intersection observer for active section tracking
  const sectionIds = allPills.map(p => p.id);
  observer = new IntersectionObserver(
    entries => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          activeSection.value = entry.target.id;
        }
      }
    },
    { rootMargin: '-80px 0px -60% 0px', threshold: 0 },
  );

  sectionIds.forEach(id => {
    const el = document.getElementById(id);
    if (el) {
      observer.observe(el);
    }
  });

  // Sticky detection via sentinel
  if (pillsRef.value) {
    stuckObserver = new IntersectionObserver(
      ([entry]) => {
        isStuck.value = !entry.isIntersecting;
      },
      { threshold: 1 },
    );
    stuckObserver.observe(pillsRef.value);
  }
});

onBeforeUnmount(() => {
  if (observer) {
    observer.disconnect();
  }
  if (stuckObserver) {
    stuckObserver.disconnect();
  }
});
</script>

<style scoped>
.scroll-pills {
  display: none;
}

@media (max-width: 768px) {
  .scroll-pills {
    display: flex;
    gap: 0.5rem;
    padding: 0 0;
    margin-top: -1rem;
    margin-bottom: 1.75rem;
    position: sticky;
    top: 0;
    z-index: 100;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
    transition: background-color 0.2s ease, box-shadow 0.2s ease;
  }

  .scroll-pills::-webkit-scrollbar {
    display: none;
  }

  .scroll-pills--stuck {
    background-color: var(--palette-color-base-white);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    padding: 0.5rem 1rem;
    margin-left: -15px;
    margin-right: -15px;
  }
}

.scroll-pill {
  flex-shrink: 0;
  padding: 0.375rem 0.875rem;
  border-radius: 999px;
  border: 1px solid var(--palette-color-default-lighten-80);
  background: var(--palette-color-base-white);
  color: var(--palette-color-default);
  font-size: 0.8125rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s ease;
  white-space: nowrap;
  line-height: 1.4;
}

.scroll-pill:hover {
  border-color: var(--palette-color-brand-primary);
  color: var(--palette-color-brand-primary);
  background: var(--palette-color-core-1-lighten-90);
}

.scroll-pill--active {
  background: var(--palette-color-brand-primary);
  color: var(--palette-color-base-white);
  border-color: var(--palette-color-brand-primary);
}

.scroll-pill--active:hover {
  background: var(--palette-color-brand-primary);
  color: var(--palette-color-base-white);
}
</style>
